import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  LuBriefcase,
  LuCalendarCheck,
  LuCalendarPlus,
  LuChartGantt,
  LuCheck,
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuCircleHelp,
  LuFileSpreadsheet,
  LuFilter,
} from 'react-icons/lu';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { BAR_TONES, JOB_STATE_TONES, PROJECT_STRIPES, PROJECT_SWATCHES, VISIT_STATE_TONES } from '../../calendarTones';
import { CAL, COL_W, GRID, barRect, cellRect } from '../stageLayout';
import { PROJECTS, TODAY, VISITS, dateOfCell, dayLists, monthDays, sceneJobs } from '../mockData';

const abs = (rect) => ({ left: rect.x, top: rect.y, width: rect.w, height: rect.h });

const CHIP_TONE = {
  planned: VISIT_STATE_TONES.planned.chip,
  inProgress: VISIT_STATE_TONES.inProgress.chip,
  overdue: VISIT_STATE_TONES.overdue.chip,
  completed: VISIT_STATE_TONES.completedDay.chip,
};
const STATUS_KEY = {
  planned: 'legend.planned',
  inProgress: 'status.ongoing',
  overdue: 'status.notCompleted',
  completed: 'legend.completed',
};

/** The visits on screen for a story state (the new one hidden until saved). */
export const sceneVisits = ({ newVisit = 'hidden', hideCompleted = false }) => VISITS
  .filter((visit) => !visit.isNew || newVisit !== 'hidden')
  .map((visit) => (visit.isNew && newVisit === 'completed' ? { ...visit, state: 'completed', done: visit.mark } : visit))
  .filter((visit) => !(hideCompleted && visit.state === 'completed'));

const Pill = ({ rect, active, icon: Icon, children, tone = 'default' }) => (
  <span
    style={abs(rect)}
    className={`tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-border tw-text-xs tw-font-semibold tw-transition-colors tw-duration-300 ${
      active
        ? tone === 'amber'
          ? 'tw-border-amber-500/30 tw-bg-amber-400/15 tw-text-amber-800 dark:tw-text-amber-200'
          : 'tw-border-teal-500/30 tw-bg-teal-500/10 tw-text-teal-800 dark:tw-border-teal-400/30 dark:tw-text-teal-200'
        : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-300'
    }`}
  >
    {active ? <LuCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" /> : Icon && <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
    <span className="tw-truncate">{children}</span>
  </span>
);

Pill.propTypes = { rect: PropTypes.object.isRequired, active: PropTypes.bool, icon: PropTypes.elementType, children: PropTypes.node, tone: PropTypes.string };

const VisitBar = ({ visit, showWindows, highlight }) => {
  const rect = barRect(visit, showWindows);
  const tone = BAR_TONES[visit.state];
  const span = showWindows ? visit.end - visit.start + 1 : 1;
  const from = showWindows ? visit.start : visit.mark;
  const at = (col) => ({ left: `${((col - from) / span) * 100}%`, width: `${100 / span}%` });
  return (
    <span
      style={abs(rect)}
      className={`tw-absolute tw-flex tw-items-center tw-overflow-hidden tw-rounded-md tw-border-l-[3px] tw-text-[11px] tw-leading-none tw-transition-all tw-duration-500 ${tone.bar} ${PROJECT_STRIPES[PROJECTS[visit.project].slot]} ${
        highlight ? 'tw-ring-2 tw-ring-teal-400 tw-ring-offset-1 dark:tw-ring-offset-[#1a1d24]' : ''
      }`}
    >
      <span aria-hidden="true" className={`tw-absolute tw-inset-y-0 ${tone.mark}`} style={at(visit.mark)} />
      {visit.done !== undefined && (
        <span aria-hidden="true" className="tw-absolute tw-inset-y-0 tw-grid tw-place-items-center" style={at(visit.done)}>
          <span className="tw-grid tw-h-4 tw-w-4 tw-place-items-center tw-rounded-full tw-bg-emerald-500 tw-text-white tw-shadow-sm">
            <LuCheck className="tw-h-2.5 tw-w-2.5" strokeWidth={3.5} />
          </span>
        </span>
      )}
      <span className="tw-relative tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-px-1.5">
        <span className="tw-shrink-0 tw-rounded tw-bg-white/75 tw-px-1 tw-py-px tw-text-[10px] tw-font-bold dark:tw-bg-black/25">V{visit.number}</span>
        <span className="tw-truncate tw-font-semibold">{visit.patient}</span>
        {rect.w > 300 && <span className="tw-truncate tw-font-normal tw-opacity-70">· {PROJECTS[visit.project].name}</span>}
      </span>
    </span>
  );
};

VisitBar.propTypes = { visit: PropTypes.object.isRequired, showWindows: PropTypes.bool, highlight: PropTypes.bool };

const VisitCard = ({ visit, base, subtle }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const visitDay = dateOfCell(base, visit.row, visit.mark).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  return (
    <div className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-l-4 tw-border-slate-200 tw-bg-white/80 dark:tw-border-white/10 dark:tw-bg-white/[0.04] ${PROJECT_STRIPES[PROJECTS[visit.project].slot]} ${subtle ? 'tw-p-2' : 'tw-p-2.5'}`}>
      <span className={`tw-grid tw-shrink-0 tw-place-items-center tw-rounded-xl tw-text-xs tw-font-bold ${subtle ? 'tw-h-9 tw-w-9' : 'tw-h-10 tw-w-10'} ${CHIP_TONE[visit.state]}`}>
        {visit.state === 'completed' ? <LuCheck className="tw-h-4 tw-w-4" strokeWidth={3} /> : visit.number}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-block tw-text-[13px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{visit.patient}</span>
        <span className="tw-block tw-truncate tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">
          {PROJECTS[visit.project].name} · {subtle ? `${label('tooltip.visitDay')}: ${visitDay}` : label('dayLabel.first')}
        </span>
        {!subtle && <span className="tw-block tw-text-[11px] tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">{label(STATUS_KEY[visit.state])}</span>}
      </span>
    </div>
  );
};

VisitCard.propTypes = { visit: PropTypes.object.isRequired, base: PropTypes.instanceOf(Date).isRequired, subtle: PropTypes.bool };

/** A job in the day panel; a linked job shows its project and colour stripe. */
const JobCard = ({ job, highlight }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const project = job.project ? PROJECTS[job.project] : null;
  return (
    <div className={`tw-relative tw-flex tw-items-center tw-gap-3 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-300 tw-bg-white/60 tw-p-2.5 tw-transition-shadow tw-duration-300 dark:tw-border-white/15 dark:tw-bg-white/[0.03] ${project ? 'tw-border-l-0 tw-pl-3.5' : ''} ${highlight ? 'tw-ring-2 tw-ring-teal-400' : ''}`}>
      {project && <span className={`tw-absolute tw-inset-y-0 tw-left-0 tw-w-1 ${PROJECT_SWATCHES[project.slot]}`} />}
      <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-slate-500/10 ${job.done ? JOB_STATE_TONES.done.text : JOB_STATE_TONES.planned.text}`}>
        {job.done ? <LuCheck className="tw-h-4 tw-w-4" strokeWidth={3} /> : <LuBriefcase className="tw-h-4 tw-w-4" />}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className={`tw-block tw-truncate tw-text-[13px] tw-font-semibold tw-text-slate-900 dark:tw-text-white ${job.done ? 'tw-line-through tw-opacity-70' : ''}`}>{t(`profilePage.tutorial.stage.${job.titleKey}`)}</span>
        <span className="tw-block tw-truncate tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">
          {project && <span className="tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">{project.name} · </span>}
          {label('jobLabel')}
        </span>
      </span>
    </div>
  );
};

JobCard.propTypes = { job: PropTypes.object.isRequired, highlight: PropTypes.bool };

const DayPanel = ({ base, day, visits, jobs, highlight, highlightJob, showToday }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const date = dateOfCell(base, day.row, day.col);
  const lists = dayLists(day, visits, jobs);
  const isToday = showToday && day.row === TODAY.row && day.col === TODAY.col;
  const box = (key) => (highlight === key ? 'tw-rounded-2xl tw-bg-teal-500/[0.07] tw-ring-2 tw-ring-teal-400/70' : '');
  return (
    <div style={abs(CAL.panel)} className="tw-absolute tw-space-y-3 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-p-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
      <h4 className="tw-text-sm tw-font-semibold tw-capitalize tw-text-slate-900 dark:tw-text-white">
        {date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
        {isToday && <span className="tw-ml-2 tw-rounded-full tw-bg-teal-500/15 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-normal-case tw-text-teal-700 dark:tw-text-teal-300">{label('today')}</span>}
      </h4>
      <div className={`tw-space-y-2 tw-p-1 tw-transition-all tw-duration-300 ${box('planned')}`}>
        <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.1em] tw-text-slate-500 dark:tw-text-slate-400">{label('agenda.planned')}</p>
        {lists.planned.length === 0 && lists.jobs.length === 0 ? (
          <p className="tw-rounded-xl tw-bg-slate-500/[0.06] tw-px-3 tw-py-2 tw-text-xs tw-text-slate-500 dark:tw-bg-white/[0.04] dark:tw-text-slate-400">{label('agenda.nothingPlanned')}</p>
        ) : (
          <>
            {lists.planned.map((visit) => <VisitCard key={visit.id} visit={visit} base={base} />)}
            {lists.jobs.map((job) => <JobCard key={job.id} job={job} highlight={highlightJob === job.id} />)}
          </>
        )}
      </div>
      {lists.windows.length > 0 && (
        <div className={`tw-space-y-2 tw-p-1 tw-transition-all tw-duration-300 ${box('windows')}`}>
          <div>
            <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.1em] tw-text-slate-500 dark:tw-text-slate-400">{label('agenda.openWindows', { count: lists.windows.length })}</p>
            <p className="tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{label('agenda.openWindowsHint')}</p>
          </div>
          {lists.windows.map((visit) => <VisitCard key={visit.id} visit={visit} base={base} subtle />)}
        </div>
      )}
    </div>
  );
};

DayPanel.propTypes = {
  base: PropTypes.instanceOf(Date).isRequired,
  day: PropTypes.object.isRequired,
  visits: PropTypes.array.isRequired,
  jobs: PropTypes.array.isRequired,
  highlight: PropTypes.string,
  highlightJob: PropTypes.string,
  showToday: PropTypes.bool,
};

/**
 * The profile calendar as drawn in the video: same toolbar, summary, bars and
 * day panel as the real one, driven by `state` instead of data.
 */
const CalendarScene = ({ state }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const { monthShift = 0, showWindows = true, hideCompleted = false, selected = TODAY, legendOpen = false, tooltip = null, highlightBar = null, panelHighlight = null } = state;

  const base = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + monthShift, 1);
  }, [monthShift]);
  const days = useMemo(() => monthDays(base), [base]);
  const visits = monthShift === 0 ? sceneVisits(state) : [];
  const jobs = monthShift === 0 ? sceneJobs(state) : [];
  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => new Date(2024, 0, 1 + index).toLocaleDateString(locale, { weekday: 'short' })),
    [locale]
  );

  const counts = visits.reduce((acc, visit) => ({ ...acc, total: acc.total + 1, [visit.state]: (acc[visit.state] || 0) + 1 }), { total: 0 });
  const summary = [
    { key: 'total', value: counts.total, dot: 'tw-bg-slate-400' },
    { key: 'planned', value: counts.planned || 0, dot: VISIT_STATE_TONES.planned.dot },
    { key: 'inProgress', value: counts.inProgress || 0, dot: VISIT_STATE_TONES.inProgress.dot },
    { key: 'overdue', value: counts.overdue || 0, dot: VISIT_STATE_TONES.overdue.dot },
    { key: 'completed', value: counts.completed || 0, dot: VISIT_STATE_TONES.completedDay.dot },
  ];
  const tooltipVisit = tooltip ? visits.find((visit) => visit.id === tooltip) : null;
  const navGroups = ['takvim-ajanda', 'dosyalar', 'projelerim', 'izin-yonetimi', 'masraf-yonetimi'];

  return (
    <div className="tw-absolute tw-inset-0 tw-bg-[#f4f6f9] dark:tw-bg-[#181b21]">
      {/* Profil bölümleri */}
      <div className="tw-absolute tw-left-6 tw-right-6 tw-top-2 tw-flex tw-h-[42px] tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-white tw-bg-white/80 tw-px-1 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.05]">
        {navGroups.map((key, index) => (
          <span
            key={key}
            className={`tw-inline-flex tw-h-[34px] tw-items-center tw-rounded-full tw-px-4 tw-text-[13px] tw-font-semibold ${index === 0 ? 'tw-w-[104px] tw-justify-center tw-bg-teal-500/15 tw-text-teal-800 dark:tw-text-teal-200' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}
          >
            {t(`profilePage.nav.groups.${key}`)}
          </span>
        ))}
        <span className="tw-ml-auto tw-mr-3 tw-text-xs tw-font-semibold tw-text-slate-400">{t('profilePage.tutorial.stage.profile')} › {t('profilePage.nav.tabs.takvim')}</span>
      </div>

      {/* Ay gezinme ve işlemler */}
      <span style={abs(CAL.prev)} className="tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-border tw-border-white tw-bg-white/80 tw-text-slate-600 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-slate-300"><LuChevronLeft className="tw-h-[18px] tw-w-[18px]" /></span>
      <span style={abs(CAL.title)} className="tw-absolute tw-grid tw-place-items-center tw-text-xl tw-font-semibold tw-capitalize tw-tracking-tight tw-text-slate-900 dark:tw-text-white">
        {base.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
      </span>
      <span style={abs(CAL.next)} className="tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-border tw-border-white tw-bg-white/80 tw-text-slate-600 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-slate-300"><LuChevronRight className="tw-h-[18px] tw-w-[18px]" /></span>
      {monthShift !== 0 && (
        <span style={abs(CAL.today)} className="tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
          <LuCalendarCheck className="tw-h-4 tw-w-4" />
          {label('today')}
        </span>
      )}
      <span style={abs(CAL.segmented)} className="tw-absolute tw-flex tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/70 tw-p-0.5 tw-text-xs tw-font-semibold dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
        <span className="tw-grid tw-flex-1 tw-place-items-center tw-rounded-full tw-bg-white tw-text-slate-900 tw-shadow-sm dark:tw-bg-white/15 dark:tw-text-white">{label('views.month')}</span>
        <span className="tw-grid tw-flex-1 tw-place-items-center tw-text-slate-500 dark:tw-text-slate-400">{label('views.list')}</span>
      </span>
      <Pill rect={CAL.excel} icon={LuFileSpreadsheet}>{label('actions.excel')}</Pill>
      <Pill rect={CAL.addJob} icon={LuBriefcase}>{label('actions.addJob')}</Pill>
      <span style={abs(CAL.addVisit)} className="tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-xs tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
        <LuCalendarPlus className="tw-h-4 tw-w-4" />
        {label('actions.addVisit')}
      </span>

      {/* Ayın özeti ve görünüm seçenekleri */}
      <div style={abs(CAL.summary)} className="tw-absolute tw-flex tw-items-center tw-gap-2 tw-overflow-hidden">
        {summary.map((item) => (
          <span key={item.key} className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-1.5 tw-text-xs dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
            <span className={`tw-h-2 tw-w-2 tw-rounded-full ${item.dot}`} />
            <span className="tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{item.value}</span>
            <span className="tw-text-slate-500 dark:tw-text-slate-400">{label(`summary.${item.key}`)}</span>
          </span>
        ))}
      </div>
      <Pill rect={CAL.windows} active={showWindows} icon={LuChartGantt}>{label('options.windows')}</Pill>
      <Pill rect={CAL.hideCompleted} active={hideCompleted} icon={LuCircleCheck} tone="amber">{label('options.hideCompleted')}</Pill>
      <Pill rect={CAL.projects} icon={LuFilter}>{label('projectFilter.button')}</Pill>
      <Pill rect={CAL.help} active={legendOpen} icon={LuCircleHelp}>{label('help.button')}</Pill>

      {/* Ay ızgarası */}
      <div style={{ left: GRID.x, top: GRID.y, width: GRID.w, height: GRID.header + GRID.rowH * GRID.rows }} className="tw-absolute tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/60 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
        <div style={{ height: GRID.header }} className="tw-grid tw-grid-cols-7 tw-border-b tw-border-slate-200 tw-bg-slate-50/80 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
          {weekdays.map((weekday, index) => (
            <span key={weekday + index} className={`tw-grid tw-place-items-center tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide ${index >= 5 ? 'tw-text-slate-400' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}>{weekday}</span>
          ))}
        </div>
      </div>
      {days.map(({ date, inMonth }, index) => {
        const row = Math.floor(index / 7);
        const col = index % 7;
        const cell = cellRect(row, col);
        const isToday = row === TODAY.row && col === TODAY.col && monthShift === 0;
        const isSelected = selected && row === selected.row && col === selected.col && monthShift === 0;
        return (
          <React.Fragment key={index}>
            <span
              style={{ left: cell.x, top: cell.y, width: COL_W, height: GRID.rowH }}
              className={`tw-absolute tw-border-slate-200/80 tw-transition-colors tw-duration-300 dark:tw-border-white/[0.06] ${col < 6 ? 'tw-border-r' : ''} ${row < 4 ? 'tw-border-b' : ''} ${
                isSelected ? 'tw-bg-teal-500/[0.07] tw-shadow-[inset_0_0_0_2px_rgba(20,184,166,0.55)]' : inMonth ? '' : 'tw-bg-slate-50/70 dark:tw-bg-black/10'
              }`}
            />
            <span
              style={{ left: cell.x + 6, top: cell.y + 5 }}
              className={`tw-absolute tw-grid tw-h-[26px] tw-min-w-[28px] tw-place-items-center tw-rounded-full tw-px-1 tw-text-xs tw-font-semibold tw-tabular-nums ${
                isToday ? 'tw-bg-teal-600 tw-text-white' : isSelected ? 'tw-bg-slate-900 tw-text-white dark:tw-bg-white dark:tw-text-slate-900' : inMonth ? 'tw-text-slate-700 dark:tw-text-slate-200' : 'tw-text-slate-300 dark:tw-text-slate-600'
              }`}
            >
              {date.getDate()}
            </span>
          </React.Fragment>
        );
      })}
      {jobs.map((job) => (
        <span key={job.id} style={abs(barRect(job))} className={`tw-absolute tw-flex tw-items-center tw-gap-1 tw-overflow-hidden tw-rounded-md tw-border tw-border-dashed tw-border-slate-300 tw-bg-white/80 tw-px-1.5 tw-text-[11px] tw-font-semibold dark:tw-border-white/15 dark:tw-bg-white/[0.05] ${job.done ? 'tw-text-emerald-700 dark:tw-text-emerald-300' : 'tw-text-indigo-700 dark:tw-text-indigo-300'} ${job.project ? 'tw-border-l-0 tw-pl-2' : ''} ${highlightBar === job.id ? 'tw-ring-2 tw-ring-teal-400' : ''}`}>
          {job.project && <span className={`tw-absolute tw-inset-y-0 tw-left-0 tw-w-[3px] ${PROJECT_SWATCHES[PROJECTS[job.project].slot]}`} />}
          <LuBriefcase className="tw-h-3 tw-w-3 tw-shrink-0" />
          <span className={`tw-truncate ${job.done ? 'tw-line-through' : ''}`}>{t(`profilePage.tutorial.stage.${job.titleKey}`)}</span>
        </span>
      ))}
      {visits.map((visit) => <VisitBar key={visit.id} visit={visit} showWindows={showWindows} highlight={highlightBar === visit.id} />)}

      <DayPanel base={base} day={selected || TODAY} visits={visits} jobs={jobs} highlight={panelHighlight} highlightJob={highlightBar} showToday={monthShift === 0} />

      {legendOpen && <Legend />}
      {tooltipVisit && <BarTooltip visit={tooltipVisit} base={base} />}
    </div>
  );
};

CalendarScene.propTypes = { state: PropTypes.object.isRequired };

/** "Nasıl okunur?" popover: what the colours mean. */
const Legend = () => {
  const { t } = useTranslation();
  const label = (key) => t(`profilePage.calendar.${key}`);
  const rows = [['planned', 'legend.planned'], ['inProgress', 'legend.inProgress'], ['overdue', 'legend.overdue'], ['completed', 'legend.completed']];
  return (
    <div style={{ left: 916, top: 160, width: 324 }} className="tw-absolute tw-z-40 tw-space-y-3 tw-rounded-2xl tw-border tw-border-white/80 tw-bg-white/95 tw-p-4 tw-shadow-2xl dark:tw-border-white/10 dark:tw-bg-[#262a34]/95">
      <p className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('help.title')}</p>
      <div className="tw-space-y-2">
        {rows.map(([state, key]) => (
          <div key={state} className="tw-flex tw-items-center tw-gap-3">
            <span className={`tw-relative tw-h-4 tw-w-16 tw-overflow-hidden tw-rounded tw-border-l-[3px] tw-border-l-slate-400 ${BAR_TONES[state].bar}`}>
              <span className={`tw-absolute tw-inset-y-0 tw-left-5 tw-w-5 ${BAR_TONES[state].mark}`} />
            </span>
            <span className="tw-text-xs tw-font-medium tw-text-slate-700 dark:tw-text-slate-200">{label(key)}</span>
          </div>
        ))}
      </div>
      <p className="tw-text-[11px] tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{label('help.bar')}</p>
    </div>
  );
};

/** The details shown when hovering a bar. */
const BarTooltip = ({ visit, base }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const rect = barRect(visit);
  const short = (col) => dateOfCell(base, visit.row, col).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const lines = [
    [label('tooltip.project'), PROJECTS[visit.project].name],
    [label('tooltip.patient'), visit.patient],
    [label('tooltip.visit'), label('visitOrdinal', { n: visit.number })],
    [label('tooltip.visitDay'), dateOfCell(base, visit.row, visit.mark).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long' })],
    [label('tooltip.window'), `${short(visit.start)} – ${short(visit.end)}`],
    [label('tooltip.status'), label(STATUS_KEY[visit.state])],
  ];
  return (
    <div style={{ left: Math.min(rect.x + rect.w / 2 - 40, 640), top: rect.y + rect.h + 8, width: 250 }} className="tw-absolute tw-z-40 tw-space-y-1 tw-rounded-xl tw-bg-slate-900/95 tw-p-3 tw-text-[11px] tw-text-white tw-shadow-xl dark:tw-bg-black/90">
      {lines.map(([key, value]) => (
        <p key={key} className="tw-flex tw-gap-2"><span className="tw-w-[5.5rem] tw-shrink-0 tw-text-white/60">{key}</span><span className="tw-font-semibold">{value}</span></p>
      ))}
    </div>
  );
};

BarTooltip.propTypes = { visit: PropTypes.object.isRequired, base: PropTypes.instanceOf(Date).isRequired };

export default CalendarScene;
